/**
 * Управляющий движением.
 *
 * Ставит на <html> атрибут data-motion, по которому CSS гасит всё
 * бесконечное. Один атрибут — один рубильник: и системная настройка,
 * и замер производительности, и ручной тумблер в настройках приходят
 * в одно место, а компоненты не знают ни о чём.
 *
 * Зачем замер вообще.
 * Достоверного способа спросить у браузера «слабое ли это устройство»
 * не существует. Battery Status API удалён из Firefox и не имплементирован
 * в Safari — его отозвали из-за фингерпринтинга. navigator.deviceMemory
 * и hardwareConcurrency есть в Chromium и отсутствуют в WKWebView, то есть
 * на половине наших пользователей. Единственный сигнал, работающий везде
 * и отражающий реальность конкретного телефона в конкретный момент, —
 * измерить, сколько на самом деле длится кадр.
 *
 * Модуль ничего не знает ни о Telegram, ни о приложении. Приостановку
 * на время сворачивания включает тот, кто про Telegram знает.
 */

/**
 * Уровней ровно два, и это осознанно.
 *
 * Промежуточный уровень пришлось бы описывать словами «немного меньше
 * тумана», а это не решение, а настройка. Либо атмосфера живёт, либо она
 * замирает — и замершая тоже выглядит намеренной, потому что зерно,
 * виньетка и тени остаются на месте.
 */
export type MotionLevel = 'full' | 'calm'

const ATTR = 'data-motion'

/** Кадров в замере. 90 — около полутора секунд при 60 Гц. */
const SAMPLE_FRAMES = 90

/**
 * Задержка перед замером.
 *
 * Первые кадры после старта заняты монтированием React, разбором CSS
 * и декодированием шрифтов. Мерить их — значит объявить слабым каждое
 * устройство подряд.
 */
const WARMUP_MS = 700

/**
 * Медиана длительности кадра, выше которой уходим в calm. 21 мс ≈ 47 fps.
 *
 * Медиана, а не среднее: одна затычка на 400 мс (сборка мусора, декод
 * картинки) уводит среднее в calm на ровном месте, а медиану — нет.
 */
const SLOW_FRAME_MS = 21

const reduceQuery = (): MediaQueryList | null =>
  typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null

/** Текущий уровень. Читается из DOM, а не из переменной: DOM — источник правды. */
export function motionLevel(): MotionLevel {
  if (typeof document === 'undefined') return 'full'
  return document.documentElement.getAttribute(ATTR) === 'calm' ? 'calm' : 'full'
}

function apply(level: MotionLevel): void {
  document.documentElement.setAttribute(ATTR, level)
}

/**
 * Запустить управляющего. Возвращает функцию остановки.
 *
 * Порядок решений (первое сработавшее выигрывает):
 *   1. Системная настройка «уменьшить движение» — уважается безоговорочно
 *      и отменяет замер: человек уже сказал, чего хочет.
 *   2. Замер частоты кадров.
 *
 * Ручной выбор пользователя сильнее обоих — см. setMotionLevel.
 */
export function startMotionGovernor(): () => void {
  if (typeof document === 'undefined' || typeof requestAnimationFrame !== 'function') {
    return () => {}
  }

  let stopped = false
  let timer: ReturnType<typeof setTimeout> | undefined
  const mq = reduceQuery()

  const onPrefChange = (): void => {
    if (manual !== null) return
    apply(mq?.matches ? 'calm' : 'full')
  }

  if (mq?.matches) {
    apply('calm')
  } else {
    apply('full')
    timer = setTimeout(() => {
      if (!stopped) probe()
    }, WARMUP_MS)
  }

  mq?.addEventListener('change', onPrefChange)

  function probe(): void {
    const frames: number[] = []
    let prev = performance.now()

    const tick = (now: number): void => {
      if (stopped) return

      const delta = now - prev
      prev = now

      // Кадры длиннее 200 мс — это не медленный рендер, а пауза: вкладку
      // свернули, пришёл звонок, система усыпила webview. Такие в статистику
      // не идут, иначе замер превращается в детектор сворачиваний.
      if (delta < 200) frames.push(delta)

      if (frames.length < SAMPLE_FRAMES) {
        requestAnimationFrame(tick)
        return
      }

      frames.sort((a, b) => a - b)
      const median = frames[Math.floor(frames.length / 2)] ?? 0

      if (manual === null && median > SLOW_FRAME_MS) apply('calm')
    }

    requestAnimationFrame(tick)
  }

  return () => {
    stopped = true
    if (timer !== undefined) clearTimeout(timer)
    mq?.removeEventListener('change', onPrefChange)
  }
}

/** Выбор человека. null — решает управляющий. */
let manual: MotionLevel | null = null

/**
 * Ручное переключение из настроек приложения.
 *
 * Сильнее и замера, и системной настройки: если человек в нашем интерфейсе
 * попросил движение — он получает движение, даже когда телефон медленный.
 * Обратное решение было бы за него, а не для него.
 *
 * 'auto' возвращает управление замеру.
 */
export function setMotionLevel(level: MotionLevel | 'auto'): void {
  if (typeof document === 'undefined') return

  if (level === 'auto') {
    manual = null
    apply(reduceQuery()?.matches ? 'calm' : 'full')
    return
  }

  manual = level
  apply(level)
}
